import * as Inovx from '@shared';
import assign from 'object-assign';
import { apiPostForm, apiPostFormUrlEncoded, FormUrlEncodedBody } from '@shared/api/apiClient';

var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var url = UrlUtilities.getApiUrl() + '/FacilityApi/api/facility';
var ontologyUrl = UrlUtilities.getApiUrl() + '/FacilityApi/api/ontology';
var tileUrl = UrlUtilities.getApiUrl() + '/FacilityApi/api/Tile3D';
var DateTimeOffsetHelper = Inovx.Shared.Common.DateTimeOffsetHelper;

interface IOntologyPartApiData extends IVersionedItem {
    FacilityID: string;
    OntologyParentID: string;
    Map?: ISiteMap;
    TransformationType: TransformationType;
    MapType: MapType;
    GpsTransformationType: GpsTransformationType;
    GpsTransformation: IGpsTransformation | null;
}

const createOntologyPartApiFormData = (data: IOntologyPartApiData) => {
    const form_data = new FormData();

    if (data.ID) form_data.append("ID", data.ID);
    if (data.VersionToken) form_data.append("VersionToken", data.VersionToken);

    form_data.append("Name", data.Name);
    form_data.append("FacilityID", data.FacilityID);
    form_data.append("OntologyParentID", data.OntologyParentID ?? "");
    form_data.append("TransformationType", data.TransformationType.toString());
    form_data.append("MapType", data.MapType.toString());

    if (data.Map && (data.Map.MapFile || data.Map.MapFileImageUrl)) {
        form_data.append("Map.Origin.X", data.Map.Origin.X.toString());
        form_data.append("Map.Origin.Y", data.Map.Origin.Y.toString());
        form_data.append("Map.Scale", data.Map.Scale.toString());
        form_data.append("Map.Rotation", data.Map.Rotation.toString());
        form_data.append("Map.ImageSize.X", data.Map.ImageSize.X.toString());
        form_data.append("Map.ImageSize.Y", data.Map.ImageSize.Y.toString());
        form_data.append("Map.RealWorldMin.X", data.Map.RealWorldMin.X.toString());
        form_data.append("Map.RealWorldMin.Y", data.Map.RealWorldMin.Y.toString());
        form_data.append("Map.RealWorldMax.X", data.Map.RealWorldMax.X.toString());
        form_data.append("Map.RealWorldMax.Y", data.Map.RealWorldMax.Y.toString());
        form_data.append("Map.MapFile", data.Map.MapFile);
        form_data.append("Map.MapFileImageUrl", data.Map.MapFileImageUrl);
        form_data.append("Map.ImageMarkers[0].X", data.Map.ImageTranslation.X.toString());
        form_data.append("Map.ImageMarkers[0].Y", data.Map.ImageTranslation.Y.toString());
        form_data.append("Map.ImageMarkers[1].X", data.Map.ImageScale.X.toString());
        form_data.append("Map.ImageMarkers[1].Y", data.Map.ImageScale.Y.toString());
        form_data.append("Map.RealWorldMarkers[0].X", data.Map.WorldTranslation.X.toString());
        form_data.append("Map.RealWorldMarkers[0].Y", data.Map.WorldTranslation.Y.toString());
        form_data.append("Map.RealWorldMarkers[1].X", data.Map.WorldScale.X.toString());
        form_data.append("Map.RealWorldMarkers[1].Y", data.Map.WorldScale.Y.toString());
    }

    form_data.append("GpsTransformationType", data.GpsTransformationType.toString());
    if (data.GpsTransformationType === "transformation" && data.GpsTransformation?.ControlPoints?.length > 0) {
        data.GpsTransformation.ControlPoints.forEach((point, index) => {
            form_data.append(`GpsTransformation.ControlPoints[${index}].Wgs84Lat`, point.Wgs84Lat.toString());
            form_data.append(`GpsTransformation.ControlPoints[${index}].Wgs84Long`, point.Wgs84Long.toString());
            form_data.append(`GpsTransformation.ControlPoints[${index}].Wgs84Alt`, point.Wgs84Alt.toString());
            form_data.append(`GpsTransformation.ControlPoints[${index}].FacilityN`, point.FacilityN.toString());
            form_data.append(`GpsTransformation.ControlPoints[${index}].FacilityE`, point.FacilityE.toString());
            form_data.append(`GpsTransformation.ControlPoints[${index}].FacilityU`, point.FacilityU.toString());
        });
    }

    return form_data;
}

var FacilityServices = assign({}, Inovx.Shared.Flux.BaseService, {
    getAll: function () {
        return new Promise<IFacility[]>((resolve, reject) => {
            this.get(url,
                null,
                (retData: IFacility[]) => {
                    resolve(retData);
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    getFacilityDetail: function (id: string) {
        return new Promise<IFacilityDetail>((resolve, reject) => {
            this.get(`${url}/Detail`,
                { id },
                (retData: IFacilityDetail) => {
                    resolve(retData)
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    getOntologyTrees: function (
        ontologyID: string,
        contentTypes: string[]
    ): Promise<string> {

        return new Promise<string>((resolve, reject) => {
            // Build content types query
            const contentTypesQuery = contentTypes
                .map(type => `ContentTypes=${encodeURIComponent(type)}`)
                .join("&");

            // Build the full URL with bounding box and content types
            const query = `ontologyID=${encodeURIComponent(ontologyID)}&` +
                contentTypesQuery;

            // Call the API
            this.get(`${tileUrl}/GetTreesWithContent?${query}`,
                null,
                (retData: string) => {
                    resolve(retData);
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    createFacility: function (facility: any) {
        return new Promise<IFacility>((resolve, reject) => {
            apiPostFormUrlEncoded<IFacility>(url + "/Create", facility as FormUrlEncodedBody, { responseType: 'text' })
            .then((result: IFacility) => {
                resolve(result);
            })
            .catch((error: any) => {
                this.handleWebApiError(error);
                reject(error);
            });

        });
    },
    
    editFacility: function (facility: any) {
        return new Promise<IFacility>((resolve, reject) => {
            apiPostFormUrlEncoded<IFacility>(url + "/Update", facility as FormUrlEncodedBody, { responseType: 'text' })
            .then((result: IFacility) => {
                resolve(result);
            })
            .catch((error: any) => {
                this.handleWebApiError(error);
                reject(error);
            });
        });
    },
    deleteFacility: function (facility: IFacility) {
        var clientTime = DateTimeOffsetHelper.getCurrentDatetimeWithOffset();
        return new Promise<void>((resolve, reject) => {
            this.post(`${url}/Delete`,
                { ID: facility.ID, VersionToken: facility.VersionToken, clientTime: clientTime },
                () => {
                    resolve();
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },

    createOntologyPart: async function (part: IOntologyPartApiData) {
        const form_data = createOntologyPartApiFormData(part);
        
        try {
            return await apiPostForm<IOntologyPartApiData>(`${ontologyUrl}/Create`, form_data, { responseType: 'json' });
        }
        catch (error) {
            this.handleWebApiError(error);
            throw error;
        }
    },
    editOntologyPart: async function (part: IOntologyPartApiData) {
        const form_data = createOntologyPartApiFormData(part);

        try {
            return await apiPostForm<IOntologyPartApiData>(`${ontologyUrl}/Update`, form_data, { responseType: 'json' });
        }
        catch (error) {
            this.handleWebApiError(error);
            throw error;
        }
    },
    deleteOntologyPart: function (part: IVersionedIdentityItem) {
        return new Promise<void>((resolve, reject) => {
            this.post(`${ontologyUrl}/Delete`,
                { ID: part.ID, VersionToken: part.VersionToken },
                () => {
                    resolve();
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },

    searchByName: function (name: string) {
        return new Promise<IFacility[]>((resolve, reject) => {
            this.get(`${url}/SearchByName`,
                { name: name },
                (retData: IFacility[]) => {
                    resolve(retData);
                },
                (error: any) => {
                    this.handleWebApiError(error);
                    reject(error);
                });
        });
    },
    getAdapters: function (id: string) {
        return new Promise<any[]>((resolve, reject) => {
            this.get(`${url}/AdapterConfigurations`,
                { facilityID: id },
                (retData: any[]) => {
                    resolve(retData);
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    synchronizeAdapterData: function (facility: IFacility, adapterType: number) {
        return new Promise<void>((resolve, reject) => {
            this.post(`${url}/SynchronizeAdapterData`,
                { facilityID: facility.ID, adapterType },
                () => {
                    resolve();
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    getGraphicSources: function (id: string) {
        return new Promise<IOntologyGraphicSource[]>((resolve, reject) => {
            this.get(`${ontologyUrl}/GraphicSources`,
                { id },
                (retData: IOntologyGraphicSource[]) => {
                    resolve(retData)
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    deleteOntologyGraphicSourceType: function (part: IOntologyGraphicSourceType) {
        const clientTime = new Date().toISOString();
        return new Promise<void>((resolve, reject) => {
            this.post(`${ontologyUrl}/DeleteGraphicSourceType?clientTime=${encodeURIComponent(clientTime)}`,
                { ontologyID: part.ontologyID, sourceType: part.sourceType },
                () => {
                    resolve();
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
    deleteOntologyGraphicSource: function (part: IOntologyGraphicSource) {
        const clientTime = new Date().toISOString(); 
        return new Promise<void>((resolve, reject) => {
            this.post(`${ontologyUrl}/DeleteGraphicSource?clientTime=${encodeURIComponent(clientTime)}`,
                { id: part.Id, sourceType: part.Type, versionToken: part.VersionToken, ontologyID: part.ontologyID },
                () => {
                    resolve();
                },
                (error: IServiceErrorObject) => {
                    this.handleWebApiError(error);
                    reject(error);
                }
            );
        });
    },
});

export default FacilityServices;